import posthog from "posthog-js";

import { API_URL, ENDPOINTS } from "@/api/application-api";
import { LOCAL_STORAGE } from "@/constants";
import { clearAuthCookie } from "@/utils/auth-cookie";
import { jsonAuthHeaders } from "@/utils/headers";

const AUTH_FAILURE_STATUS = 401;
const REDIRECT_PATH = "/login";

let logoutInProgress = false;
let authErrorHandlerInstalled = false;
let originalFetch: typeof window.fetch | null = null;

const getFetchImpl = (): typeof window.fetch => originalFetch ?? window.fetch.bind(window);

const getStoredAccessToken = (): string | null => localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN);

const persistLastLoginType = () => {
  const provider = localStorage.getItem("provider");
  if (provider) {
    localStorage.setItem("last_login_type", provider);
    localStorage.removeItem("provider");
  }
};

export const clearLocalAuthState = () => {
  clearAuthCookie();
  localStorage.removeItem(LOCAL_STORAGE.ACCESS_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE.ACTIVE_WORKSPACE_ID);
  localStorage.removeItem(LOCAL_STORAGE.USER);
  localStorage.removeItem(LOCAL_STORAGE.POSTHOG_PLAN_TYPE);
  localStorage.removeItem(LOCAL_STORAGE.WS_TOKEN);
  persistLastLoginType();
  posthog.reset();
};

const postLogoutToServer = () => {
  const accessToken = getStoredAccessToken();
  if (!accessToken) {
    return;
  }

  void getFetchImpl()(ENDPOINTS.auth.logout, {
    method: "POST",
    headers: jsonAuthHeaders(accessToken),
  }).catch(() => undefined);
};

export const logoutClientSession = ({
  redirectToLogin = false,
  notifyServer = true,
}: {
  redirectToLogin?: boolean;
  notifyServer?: boolean;
} = {}) => {
  if (logoutInProgress) {
    if (redirectToLogin && window.location.pathname !== REDIRECT_PATH) {
      window.location.assign(REDIRECT_PATH);
    }
    return;
  }

  logoutInProgress = true;

  try {
    if (notifyServer) {
      postLogoutToServer();
    }

    clearLocalAuthState();

    if (redirectToLogin && window.location.pathname !== REDIRECT_PATH) {
      window.location.assign(REDIRECT_PATH);
    }
  } finally {
    logoutInProgress = false;
  }
};

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
};

const getAuthorizationHeader = (input: RequestInfo | URL, init?: RequestInit): string | null => {
  if (init?.headers) {
    const headers = new Headers(init.headers);
    return headers.get("Authorization");
  }

  if (input instanceof Request) {
    return input.headers.get("Authorization");
  }

  return null;
};

const shouldLogoutForUnauthorizedResponse = (
  response: Response,
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean => {
  if (response.status !== AUTH_FAILURE_STATUS) {
    return false;
  }

  const requestUrl = getRequestUrl(input);
  if (requestUrl === ENDPOINTS.auth.logout) {
    return false;
  }

  const authHeader = getAuthorizationHeader(input, init);
  if (authHeader?.startsWith("Bearer ")) {
    return true;
  }

  return requestUrl.startsWith(API_URL) && !!getStoredAccessToken();
};

export const installAuthErrorHandler = () => {
  if (authErrorHandlerInstalled) {
    return;
  }

  originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch!(input, init);

    if (shouldLogoutForUnauthorizedResponse(response, input, init)) {
      logoutClientSession({ redirectToLogin: true, notifyServer: false });
    }

    return response;
  };

  authErrorHandlerInstalled = true;
};
